import {rememberAuthDestination} from '../state/authJourney';
import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchMeShared, bustMeCache, MeRequestError } from '../api/me-shared';
import type { User } from '../types';

/**
 * Cookie-only auth hook (CF rebuild).
 *
 * The CF Worker drops a session cookie on login/register; the
 * client never holds a token. Source of truth for "am I signed in?"
 * is the server: we call /api/auth/me on mount and trust the cookie.
 *
 * We still persist the *user descriptor* in localStorage as a
 * cache so the UI can render without flashing through "loading"
 * on every reload — but the cookie is the security primitive.
 */

let redirecting=false;
export function onAuthError() {
 if(redirecting)return;redirecting=true;rememberAuthDestination();bustMeCache();
 try{localStorage.removeItem('pp_user');}catch{}
 // Do not race a background logout against the user's next login.
 window.location.assign('/login');
}

/** Fetch /api/auth/me (shared/deduped) and synthesize PP's local User shape. */
async function probeMe(): Promise<User | null> {
  try {
    const data = await fetchMeShared();
    return {
      id: data.user.id,
      username: data.user.display_name || data.user.email,
      role: 'user',
      user_type: 'human',
    };
  } catch(e) {
    if(e instanceof MeRequestError&&e.status===401)return null;
    throw e;
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  // Synthetic token kept for API compatibility; the cookie is what
  // actually authenticates requests.
  const [token] = useState<string>('cookie-auth');
  const [isLoading, setIsLoading] = useState(true);
  const [error,setError]=useState('');const [attempt,setAttempt]=useState(0);const authGeneration=useRef(0);
  const retry=useCallback(()=>{setError('');setIsLoading(true);setAttempt(n=>n+1);},[]);

  const doLogout = useCallback(async () => {
    authGeneration.current++;bustMeCache();
    const response = await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    if (!response.ok) throw new Error('Could not sign out.');
    try {
      localStorage.removeItem('pp_user');
      localStorage.removeItem('pp_token'); // legacy cleanup
    } catch {
      /* ignore */
    }
    setUser(null);
  }, []);

  useEffect(() => {
    let cancelled = false;const version=authGeneration.current;
    void (async () => {
      // Optimistic render from cache, then verify with server.
      try {
        const cached = localStorage.getItem('pp_user');
        if (cached) setUser(JSON.parse(cached) as User);
      } catch {
        /* ignore */
      }
      let me:User|null;
      try{me=await probeMe();}catch{if(!cancelled&&version===authGeneration.current){setError('We could not connect to your workspace. Check your connection and try again.');setIsLoading(false);}return;}
      if (cancelled||version!==authGeneration.current) return;
      setUser(me);
      try {
        if (me) localStorage.setItem('pp_user', JSON.stringify(me));
        else localStorage.removeItem('pp_user');
      } catch {
        /* ignore */
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    const handler = () => {void doLogout().catch(()=>setError("Could not sign out. Please try again."));};
    window.addEventListener('pp:logout-requested', handler);
    return () => window.removeEventListener('pp:logout-requested', handler);
  }, [doLogout]);

  const handleLogin = useCallback(
    (loginData: {
      token: string;
      user_id: string;
      username: string;
      role: string;
    }) => {
      authGeneration.current++;bustMeCache();setError('');setIsLoading(false);
      const u: User = {
        id: loginData.user_id,
        username: loginData.username,
        role: loginData.role,
        user_type: 'human',
      };
      try {
        localStorage.setItem('pp_user', JSON.stringify(u));
      } catch {
        /* ignore */
      }
      setUser(u);
    },
    [],
  );

  return { user, token, isLoading, error, retry, handleLogin, logout: doLogout };
}
